import Icon from '@atoms/Icon/Icon';
import Modal from '@atoms/Modal/Modal';
import { ILink } from '@models/ILink';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import VerticalDirectionEnum from '@models/enums/VerticalDirectionEnum';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';

const dropdownVariants = {
  enter: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: 0.1,
      ease: 'easeInOut',
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: 'easeInOut',
    },
  },
};

interface Props {
  readonly active?: boolean;
  readonly activeIndex?: number;
  readonly animateUnderline?: boolean;
  readonly dataLayerCallback?: (label: string) => void;
  readonly dataTestId?: string;
  readonly isOpen: boolean;
  readonly itemCallback?: (label: string) => void;
  readonly links: ILink[] | string[];
  readonly modalTitle: string;
  readonly openFromDirection: VerticalDirectionEnum;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly isCountrySelector?: boolean;
  readonly title?: string;
  readonly titleCallback?: () => void;
}

const Dropdown = ({
  active = false,
  activeIndex = -1,
  animateUnderline,
  dataLayerCallback,
  dataTestId,
  isCountrySelector = false,
  isOpen,
  itemCallback,
  links,
  modalTitle,
  openFromDirection,
  setIsOpen,
  title,
  titleCallback,
}: Props) => {
  const isTouchDevice = useIsTouchDevice();
  const isInIFrame = useIsInIFrame();
  const isMobileViewport = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;

  const underlineClasses = animateUnderline
    ? 'animate-underline'
    : 'border-b-2 border-yellow';

  return isTouchDevice ? (
    <div
      className="flex flex-row items-center text-white"
      data-testid={dataTestId}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className={classNames(
          {
            'border-yellow border-b pb-0.5 text-yellow': active,
          },
          'body-two shrink-0'
        )}
        data-testid="Dropdown-Title"
      >
        {title}
      </button>
      <Icon
        name="arrow_right"
        colorClassName="text-yellow"
        className={classNames({
          '-scale-y-100': isOpen,
        })}
      />

      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        modalClasses="flex flex-col justify-start w-full h-full fixed top-0 bottom-0 left-0 right-0 pt-16 text-black-100 bg-white max-w-none"
        crossColor="black"
        contentClasses="py-4"
      >
        <div className="container h-full w-full list-none text-black-100">
          {title && <p className="h5 pb-7.5">{modalTitle}</p>}
          <ul className="flex flex-col items-start gap-y-7.5">
            {links?.length > 0 && typeof links[0] === 'object'
              ? (links as ILink[]).map(
                  ({ url, target, longName, shortName, name, lang }, index) => (
                    <li
                      className="body-two shrink-0 cursor-pointer"
                      key={`dropdown-list-item-${
                        longName || shortName || name
                      }`}
                    >
                      <a
                        href={isInIFrame ? '_parent' : url}
                        target={target}
                        className={classNames(
                          {
                            [underlineClasses]: activeIndex === index,
                          },
                          'cursor-pointer whitespace-nowrap pb-0.5 text-black-100 hover:text-grey-500'
                        )}
                        data-testid="Dropdown-Item"
                        lang={lang}
                      >
                        {longName || shortName || name}
                      </a>
                    </li>
                  )
                )
              : ((links ?? []) as string[]).map((label, index) => (
                  <button
                    key={`dropdown-list-item-${label}`}
                    onClick={() => {
                      itemCallback?.(activeIndex !== index ? label : '');
                      setIsOpen(!isOpen);
                    }}
                    className={classNames(
                      {
                        'border-yellow border-b-2': activeIndex === index,
                      },
                      'cursor-pointer whitespace-nowrap pb-0.5 text-black-100 hover:text-grey-500'
                    )}
                  >
                    {label}
                  </button>
                ))}
          </ul>
        </div>
      </Modal>
    </div>
  ) : (
    <motion.button
      aria-expanded={isOpen}
      className="relative max-w-max"
      onClick={() => {
        setIsOpen(!isOpen);
        titleCallback?.();
      }}
      onKeyDown={(e) => {
        const { key, currentTarget } = e;

        if (
          !isOpen &&
          (key === 'ArrowDown' || key === 'Enter' || key === ' ')
        ) {
          e.preventDefault(); // Prevent scroll on Space
          titleCallback?.();
          setIsOpen(true);
        }

        if (isOpen && key === 'Escape') {
          setIsOpen(false);
          currentTarget.focus();
        }
      }}
      onBlur={(e) => {
        const { currentTarget } = e;
        requestAnimationFrame(() => {
          if (!currentTarget.contains(document.activeElement)) {
            setIsOpen(false);
          }
        });
      }}
    >
      <div className="flex flex-row text-white">
        <span
          className={classNames(
            {
              'border-yellow border-b pb-0.5 text-yellow': active,
            },
            'body-two shrink-0'
          )}
          data-testid="Dropdown-Title"
        >
          {title}
        </span>
        <motion.span
          animate={{
            rotate: isOpen ? 180 : 0,
          }}
        >
          <Icon
            name="caret_down"
            colorClassName="text-yellow"
            size={IconSizeEnum.lg}
            className={classNames({
              'rotate-180': openFromDirection !== VerticalDirectionEnum.Down,
            })}
          />
        </motion.span>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.ul
            role="menu"
            className={classNames(
              'hide-scrollbar lg:-translate-x-1/2 absolute left-1/2 z-80 flex h-auto max-h-[500px] flex-col items-start gap-7.5 overflow-auto rounded-[10px] bg-white p-6 px-[25px] py-[30px]',
              {
                'top-10': openFromDirection === VerticalDirectionEnum.Down,
                'bottom-10': openFromDirection === VerticalDirectionEnum.Up,
                '-translate-x-1/2': isZoomedDesktop && !isCountrySelector,
                '-translate-x-[15%]': isZoomedDesktop && isCountrySelector,
              }
            )}
          >
            {links?.length > 0 && typeof links[0] === 'object'
              ? (links as ILink[]).map(
                  (
                    { url, target, longName, shortName, name, lang }: ILink,
                    index
                  ) => (
                    <motion.li
                      initial="exit"
                      animate="enter"
                      exit="exit"
                      variants={dropdownVariants}
                      transition={{
                        duration: 0.1,
                        ease: 'easeInOut',
                      }}
                      className="body-two shrink-0 cursor-pointer"
                      key={`dropdown-list-item-${
                        longName || shortName || name
                      }`}
                    >
                      <a
                        href={url}
                        target={target}
                        className={classNames(
                          {
                            [underlineClasses]: activeIndex === index,
                          },
                          'cursor-pointer whitespace-nowrap pb-0.5 text-black-100 hover:text-grey-500'
                        )}
                        data-testid="Dropdown-Item"
                        onClick={() => {
                          dataLayerCallback?.(
                            longName || shortName || name || ''
                          );
                        }}
                        lang={lang}
                      >
                        {longName || shortName || name}
                      </a>
                    </motion.li>
                  )
                )
              : ((links ?? []) as string[]).map((label, index) => (
                  <motion.li
                    initial="exit"
                    animate="enter"
                    exit="exit"
                    variants={dropdownVariants}
                    className="body-three w-full shrink-0 cursor-pointer leading-6"
                    key={`dropdown-list-item-${label}`}
                  >
                    <div
                      role="menuitem"
                      tabIndex={0}
                      onClick={() => {
                        itemCallback?.(activeIndex !== index ? label : '');
                        dataLayerCallback?.(label);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          itemCallback?.(activeIndex !== index ? label : '');
                          dataLayerCallback?.(label);
                        }
                      }}
                      className={classNames(
                        {
                          'border-yellow border-b-2': activeIndex === index,
                        },
                        'cursor-pointer whitespace-nowrap pb-0.5 text-black-100 hover:text-grey-500'
                      )}
                      data-testid="Dropdown-Item"
                    >
                      {label}
                    </div>
                  </motion.li>
                ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export default Dropdown;
